import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import useModal from "components/modal/useModal";
import UseContext from "hooks/useContext";
import Modalheader from "components/modal/components/header";
import ModalBody from "components/modal/components/body";
import ModalFooter from "components/modal/components/footer";
import Button from "components/button";
import LoadingButton from "components/button/loading";
import StyledInput from "components/styledInput/StyledInput";
import TemplateNamePrefix from "pages/missionTemplates/components/TemplateNamePrefix";
import FieldLabel from "components/styledInput/FieldLabel";
import ApiDataBase from "server";
import {
    buildDuplicateTemplateName,
    getTemplateIntituleSuffix,
    isTemplateNameTaken,
    templateIntitulePrefix,
} from "pages/missionTemplates/utils/templateIntitule";

const DuplicateMissionTemplate = () => {
    const { toggle } = useModal();
    const {
        pendingDuplicateMissionTemplate,
        setPendingDuplicateMissionTemplate,
        missionTemplates,
        refreshMissionTemplates,
        setRefreshMissionTemplates,
        previewMissionTemplate,
        setPreviewMissionTemplate,
        setModalType,
        language,
    } = UseContext();

    const source = pendingDuplicateMissionTemplate;
    const [name, setName] = useState(() =>
        buildDuplicateTemplateName(source?.name, missionTemplates)
    );
    const [isLoading, setIsLoading] = useState(false);
    const nameInputRef = useRef(null);

    // StyledInput has no autoFocus prop: the field is focused on open.
    useEffect(() => {
        nameInputRef.current?.focus();
    }, []);

    // Same rule as the wizard: the qualification opens the name and cannot be
    // touched, only what follows the dash is typed.
    const namePrefix = templateIntitulePrefix(source?.qualification);
    const nameSuffix = getTemplateIntituleSuffix(name, source?.qualification);
    const onSuffixChange = (suffix) => setName(namePrefix + suffix);

    const isNameTaken = useMemo(
        () => isTemplateNameTaken(name, missionTemplates),
        [name, missionTemplates]
    );
    const canSubmit = !!name.trim() && !isNameTaken && !!source?._id;

    // Opened from the preview panel: cancelling goes back to it.
    const handleCancel = () => {
        setPendingDuplicateMissionTemplate(null);
        if (previewMissionTemplate?._id) {
            setModalType("missionTemplate_preview");
            return;
        }
        toggle();
    };

    const handleConfirm = () => {
        if (!canSubmit) return;
        setIsLoading(true);
        ApiDataBase.postDuplicateMissionTemplate(
            { id: source._id, name: name.trim() },
            (created) => {
                setPendingDuplicateMissionTemplate(null);
                setRefreshMissionTemplates(!refreshMissionTemplates);
                toast.success(
                    language.getValue(
                        "missionTemplate.duplicate.done",
                        "Fiche de poste dupliquée"
                    )
                );
                // The api answers with the raw document, whose location is still
                // an id: the preview needs the read shape, so it is re-read.
                ApiDataBase.getMissionTemplate(
                    { id: created?._id },
                    (template) => {
                        setIsLoading(false);
                        setPreviewMissionTemplate(template);
                        setModalType("missionTemplate_preview");
                    },
                    (err) => {
                        setIsLoading(false);
                        console.error(err);
                        toggle();
                    }
                );
            },
            (err) => {
                setIsLoading(false);
                console.error(err);
                if (
                    err?.response?.data?.message === "Template name already used"
                ) {
                    toast.error("Une fiche de poste porte déjà ce nom");
                    return;
                }
                toast.error(
                    language.getValue(
                        "missionTemplate.duplicate.failed",
                        "Duplication impossible"
                    )
                );
            }
        );
    };

    return (
        <>
            <Modalheader
                hide={handleCancel}
                hideShow={true}
                closeLabel={language.getValue("missionTemplate.modal.close", "Fermer")}
                description={language.getValue(
                    "missionTemplate.duplicate.description",
                    "La copie reprend tout le contenu de la fiche. Donnez-lui un nom."
                )}
            >
                {language.getValue(
                    "missionTemplate.duplicate.title",
                    "Dupliquer la fiche de poste"
                )}
            </Modalheader>
            <ModalBody>
                <div className={"flex flex-col gap-2"}>
                    <div className={"flex flex-col gap-1.5"}>
                        <FieldLabel
                            label={language.getValue(
                                "missionTemplate.duplicate.nameLabel",
                                "Nom de la nouvelle fiche"
                            )}
                        />
                        {/* Prefix and field read as one: no gap, no radius where they meet. */}
                        <div className={"flex items-start"}>
                            <TemplateNamePrefix prefix={namePrefix} />
                            <div className={`flex-1 min-w-0 ${namePrefix ? "[&_input]:rounded-l-none" : ""}`}>
                                <StyledInput
                                    type={"text"}
                                    inputRef={nameInputRef}
                                    value={nameSuffix}
                                    onChange={(e) => onSuffixChange(e.target.value)}
                                    placeholder={language.getValue(
                                        "missionTemplate.duplicate.suffixPlaceholder",
                                        "Complément"
                                    )}
                                    error={
                                        isNameTaken
                                            ? language.getValue(
                                                  "missionTemplate.activity.nameTaken",
                                                  "Une fiche de poste porte déjà ce nom."
                                              )
                                            : undefined
                                    }
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </ModalBody>
            <ModalFooter>
                <Button size={"LG"} color={"SECONDARY"} onClick={handleCancel}>
                    Annuler
                </Button>
                {isLoading ? (
                    <LoadingButton color={"PRIMARY"} size={"LG"}>Duplication…</LoadingButton>
                ) : (
                    <Button
                        size={"LG"}
                        color={canSubmit ? "PRIMARY" : "DISABLED"}
                        onClick={handleConfirm}
                        disabled={!canSubmit}
                    >
                        {language.getValue(
                            "missionTemplate.duplicate.confirm",
                            "Dupliquer"
                        )}
                    </Button>
                )}
            </ModalFooter>
        </>
    );
};

export default DuplicateMissionTemplate;
